/**
 * Template Script Loader
 * 
 * Loads template-specific JavaScript based on data attributes.
 * Uses eager glob imports to avoid dynamic import issues with Shopify's CDN.
 */

// Eagerly import all template modules using Vite's glob import
const templateModules = import.meta.glob('../templates/*.js', { eager: true });

// Create a map of template name to module
const templateMap = {};
for (const path in templateModules) {
  // Extract template name from path (e.g., '../templates/product.js' -> 'product')
  const name = path.match(/\/([^/]+)\.js$/)?.[1];
  if (name) {
    templateMap[name] = templateModules[path];
  }
}

/**
 * Load template-specific scripts
 */
export function loadTemplateScripts() {
  const templates = document.querySelectorAll('[data-template-script]');
  
  templates.forEach((template) => {
    const scriptName = template.dataset.templateScript;
    
    if (scriptName) {
      loadTemplateScript(scriptName, template);
    }
  });
}

/**
 * Load a specific template script
 */
function loadTemplateScript(scriptName, element) {
  try {
    const module = templateMap[scriptName];
    
    if (!module) {
      // Template script doesn't exist, silently skip
      return;
    }
    
    if (module.default) {
      // If the module exports a default class/function, instantiate it
      if (typeof module.default === 'function') {
        new module.default(element);
      }
    } else if (module.init) {
      // If the module exports an init function, call it
      module.init(element);
    }
    
    // console.log(`📦 Loaded template script: ${scriptName}`);
  } catch (error) {
    console.warn(`Failed to load template script: ${scriptName}`, error);
  }
}

/**
 * Observer for dynamically added templates (for theme editor)
 */
export function observeTemplates() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.hasAttribute('data-template-script')) {
          const scriptName = node.dataset.templateScript;
          loadTemplateScript(scriptName, node);
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Initialize template observer
observeTemplates();

